// /groups/:slug/dashboard — engagement metrics for group admins.
// Simple bars for weekly volume + counters + top posts + recent joins.
// No charting library — CSS bars are enough for POC and skip 40kb of JS.
//
// Also renders a ClaimPanel at the top so any visitor (owner or not)
// can act on unclaimed groups OR file an appeal against a claimed one.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import type { DashboardStats, Me } from '../types/api';

interface GroupInfo {
    id: string;
    slug: string;
    name: string;
    description?: string | null;
    claim_status: 'unclaimed' | 'pending' | 'claimed' | 'disputed';
    claimed_by_user_id?: string | null;
    // Business profile fields — nullable for non-business groups.
    category?: string | null;
    address?:  string | null;
    phone?:    string | null;
    email?:    string | null;
    website?:  string | null;
    hours?:    Record<string, string> | null;
    lat?:      number | null;
    lon?:      number | null;
}

export function GroupDashboard() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const { authed, login } = useAuth();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [group, setGroup] = useState<GroupInfo | null>(null);
    const [me,    setMe]    = useState<Me | null>(null);
    const [err,   setErr]   = useState('');

    const load = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            const [g, m] = await Promise.all([
                api.get<GroupInfo>('/api/groups/' + slug),
                api.get<Me>('/api/me').catch(() => null),
            ]);
            setGroup(g);
            setMe(m);
            // Dashboard stats are admin-only; ignore 403 so non-owners still
            // see the ClaimPanel + basic group info.
            try {
                setStats(await api.get<DashboardStats>('/api/groups/' + slug + '/dashboard'));
            } catch (_) { /* not an admin — that's fine */ }
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, slug]);

    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see the dashboard.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (err) return (
        <main className="dashboard-wrap"><p style={{ color: '#8b1919' }}>{err}</p></main>
    );
    if (!group && !stats) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    return (
        <main>
            <div className="dashboard-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>{group?.name ?? stats?.group_slug ?? slug}</h1>
                    <div className="issue">
                        {group?.claim_status === 'unclaimed' && 'Unclaimed listing'}
                        {group?.claim_status === 'pending'   && 'Claim pending staff review'}
                        {group?.claim_status === 'disputed'  && 'Ownership disputed'}
                        {group?.claim_status === 'claimed' && stats && 'Engagement dashboard · last 30 days'}
                        {group?.claim_status === 'claimed' && !stats && `Claimed group · ${group.name}`}
                    </div>
                    <div className="rule-bottom" />
                </div>

                {group && me && <ClaimPanel group={group} me={me} onChanged={load} />}

                {/* Owner-only editor for the business profile fields that
                    show on /groups/:slug. Guarded on claimed_by_user_id
                    (same rule as ownership badges elsewhere). */}
                {group && me && group.claimed_by_user_id === me.id && (
                    <BusinessInfoEditor group={group} onSaved={load} />
                )}

                {group && me && <ReviewsSection groupSlug={group.slug} me={me} isOwner={group.claimed_by_user_id === me.id} />}

                {stats && <>
                <section className="stat-grid">
                    <StatCard label="Members"            total={stats.member_count}     delta={stats.new_members_30d} />
                    <StatCard label="Posts"              total={stats.total_posts}      delta={stats.posts_30d} />
                    <StatCard label="Likes"              total={stats.total_likes} delta={stats.likes_30d} />
                    <StatCard label="Comments"           total={stats.total_comments}   delta={stats.comments_30d} />
                </section>
                </>}
                {stats && <StatBlocks stats={stats} />}
            </div>
        </main>
    );
}

function StatBlocks({ stats }: { stats: DashboardStats }) {
    return <>
        <section className="dashboard-block">
            <h2>Posts per week</h2>
            <BarChart data={stats.posts_per_week} />
        </section>

        <section className="dashboard-block">
            <h2>Likes per week</h2>
            <BarChart data={stats.likes_per_week} accent />
        </section>

        <section className="dashboard-block">
            <h2>Top posts</h2>
            {stats.top_posts.length === 0 ? (
                <p className="empty">No posts yet. Publish one to see it here.</p>
            ) : (
                <table className="dashboard-table">
                    <thead>
                        <tr><th>Post</th><th>Kind</th><th>Recs</th><th>Comments</th><th>Published</th></tr>
                    </thead>
                    <tbody>
                        {stats.top_posts.map(p => (
                            <tr key={p.post_id}>
                                <td><Link to={'/posts/' + p.post_id}>{p.title || '(untitled)'}</Link></td>
                                <td className="dashboard-kind">{p.post_type}</td>
                                <td>{p.like_count}</td>
                                <td>{p.comment_count}</td>
                                <td>{timeAgo(p.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>

        <section className="dashboard-block">
            <h2>Recent joins</h2>
            {stats.recent_joins.length === 0 ? (
                <p className="empty">No members yet.</p>
            ) : (
                <table className="dashboard-table">
                    <thead>
                        <tr><th>Member</th><th>Role</th><th>Joined</th></tr>
                    </thead>
                    <tbody>
                        {stats.recent_joins.map(m => (
                            <tr key={m.user_id}>
                                <td>{m.display_name}</td>
                                <td className="dashboard-kind">{m.role}</td>
                                <td>{timeAgo(m.joined_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    </>;
}

// ClaimPanel — visible to any authed visitor of a group page.
//   * unclaimed  → "Claim this business" (opens a modal)
//   * pending    → "Claim under staff review" (info-only)
//   * disputed   → "Ownership disputed — staff reviewing" (info-only)
//   * claimed + viewer is NOT the claimant → "Dispute ownership" (appeal)
//   * claimed + viewer IS the claimant → nothing (they already own it)
function ClaimPanel({ group, me, onChanged }: { group: GroupInfo; me: Me; onChanged: () => void }) {
    const api = useApi();
    const [open, setOpen] = useState<'claim' | 'appeal' | null>(null);
    const [reason,   setReason]   = useState('');
    const [evidence, setEvidence] = useState('');
    const [status,   setStatus]   = useState('');

    const isOwner = group.claim_status === 'claimed' && group.claimed_by_user_id === me.id;
    if (isOwner) return null;

    async function submit(kind: 'claim' | 'appeal') {
        if (reason.trim().length < 10) {
            setStatus('Please explain who you are and why you own this (min 10 chars).');
            return;
        }
        setStatus('Submitting…');
        try {
            const path = kind === 'claim' ? '/claim' : '/appeal';
            await api.post('/api/groups/' + group.slug + path, { reason, evidence_url: evidence });
            setStatus('✓ Submitted. Staff will review and get back to you.');
            setReason(''); setEvidence('');
            setTimeout(() => { setOpen(null); setStatus(''); onChanged(); }, 1500);
        } catch (e) {
            setStatus('Failed: ' + (e as Error).message);
        }
    }

    let banner: React.ReactNode = null;
    if (group.claim_status === 'unclaimed') {
        banner = <>
            <span style={{ ...pillStyle, background: '#faf8f2', color: '#666', border: '1px solid #999' }}>Unclaimed</span>
            <span style={{ flex: 1, fontFamily: "'Newsreader',serif", color: '#333' }}>
                This listing has no verified owner yet.
            </span>
            <button type="button" onClick={() => setOpen('claim')} style={ctaStyle('accent')}>Claim this business</button>
        </>;
    } else if (group.claim_status === 'pending') {
        banner = <>
            <span style={{ ...pillStyle, background: '#fdf5e6', color: '#7a5a00' }}>Pending review</span>
            <span style={{ flex: 1, fontFamily: "'Newsreader',serif", color: '#333' }}>
                A claim on this listing is being reviewed by staff.
            </span>
        </>;
    } else if (group.claim_status === 'disputed') {
        banner = <>
            <span style={{ ...pillStyle, background: '#f2dfc3', color: '#8b1919' }}>Ownership disputed</span>
            <span style={{ flex: 1, fontFamily: "'Newsreader',serif", color: '#333' }}>
                Staff are reviewing a competing claim on this listing.
            </span>
        </>;
    } else if (group.claim_status === 'claimed') {
        banner = <>
            <span style={{ ...pillStyle, background: '#c6e6c9', color: '#2b5e3f' }}>Claimed</span>
            <span style={{ flex: 1, fontFamily: "'Newsreader',serif", color: '#333' }}>
                Think this listing was claimed by the wrong person?
            </span>
            <button type="button" onClick={() => setOpen('appeal')} style={ctaStyle('danger')}>Dispute ownership</button>
        </>;
    }

    return (
        <section style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                          padding: 12, border: '1px dashed #999', background: '#fdfcf6',
                          marginTop: 12, marginBottom: 18 }}>
            {banner}
            {open && (
                <div style={{ flexBasis: '100%', marginTop: 8 }}>
                    <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#666', marginBottom: 4 }}>
                        Why you own this
                    </label>
                    <textarea value={reason} onChange={e => setReason(e.target.value)}
                              rows={3}
                              placeholder="I'm the owner of Deer Park Café. I run the day-to-day operations and can be contacted on ross@deerparkcafe.co.za."
                              style={{ width: '100%', padding: 8, fontFamily: 'inherit', border: '1px solid #999' }} />
                    <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#666', margin: '10px 0 4px' }}>
                        Evidence link (optional — website, business register, LinkedIn)
                    </label>
                    <input type="url" value={evidence} onChange={e => setEvidence(e.target.value)}
                           placeholder="https://…"
                           style={{ width: '100%', padding: 8, fontFamily: 'inherit', border: '1px solid #999' }} />
                    <div style={{ display: 'flex', gap: 12, marginTop: 10, alignItems: 'center' }}>
                        <button type="button" onClick={() => submit(open)} style={ctaStyle('accent')}>
                            Submit {open === 'appeal' ? 'appeal' : 'claim'}
                        </button>
                        <button type="button" onClick={() => { setOpen(null); setStatus(''); setReason(''); setEvidence(''); }}
                                style={{ padding: '6px 12px', background: 'none', border: '1px solid #999', cursor: 'pointer' }}>
                            Cancel
                        </button>
                        {status && <span style={{ fontSize: 12, color: status.startsWith('Failed') ? '#8b1919' : '#2b5e3f' }}>{status}</span>}
                    </div>
                </div>
            )}
        </section>
    );
}

const pillStyle: React.CSSProperties = {
    display: 'inline-block', padding: '2px 8px', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: 2, fontFamily: '-apple-system, sans-serif', fontWeight: 700,
};
function ctaStyle(kind: 'accent' | 'danger'): React.CSSProperties {
    return {
        padding: '6px 14px',
        background: kind === 'danger' ? '#8b1919' : 'var(--accent)',
        color: '#fff', border: 0, cursor: 'pointer',
        fontFamily: '-apple-system, sans-serif', fontSize: 12,
        textTransform: 'uppercase', letterSpacing: 1,
    };
}

// -----------------------------------------------------------------
// Reviews — star + prose, one per author. Non-owners see a
// "Write a review" affordance; owners see the aggregate only.

interface Review {
    id: string; group_id: string; author_user_id: string;
    author_name: string; author_avatar?: string | null;
    rating: number; body?: string | null;
    created_at: string; updated_at: string;
}
interface ReviewSummary {
    average: number; count: number; my_rating?: number | null;
    distribution: [number, number, number, number, number];
}

function ReviewsSection({ groupSlug, me, isOwner }: { groupSlug: string; me: Me; isOwner: boolean }) {
    const api = useApi();
    const [summary, setSummary] = useState<ReviewSummary | null>(null);
    const [items,   setItems]   = useState<Review[]>([]);
    const [draftR,  setDraftR]  = useState<number>(0);
    const [draftB,  setDraftB]  = useState<string>('');
    const [status,  setStatus]  = useState('');

    const load = useCallback(async () => {
        try {
            const [s, l] = await Promise.all([
                api.get<ReviewSummary>('/api/groups/' + groupSlug + '/review-summary'),
                api.get<{ reviews: Review[] }>('/api/groups/' + groupSlug + '/reviews'),
            ]);
            setSummary(s);
            setItems(l.reviews ?? []);
            if (s.my_rating != null) setDraftR(s.my_rating);
        } catch (e) { console.error('reviews load', e); }
    }, [api, groupSlug]);
    useEffect(() => { load(); }, [load]);

    async function submit() {
        if (draftR < 1) { setStatus('Pick a star rating first.'); return; }
        setStatus('Saving…');
        try {
            await api.post('/api/groups/' + groupSlug + '/reviews', { rating: draftR, body: draftB });
            setDraftB('');
            setStatus('✓ Thanks — review saved.');
            setTimeout(() => setStatus(''), 3000);
            await load();
        } catch (e) { setStatus('Failed: ' + (e as Error).message); }
    }
    async function remove() {
        if (!window.confirm('Remove your review?')) return;
        try {
            await api.del('/api/groups/' + groupSlug + '/reviews');
            setDraftR(0); setDraftB('');
            await load();
        } catch (e) { setStatus('Failed: ' + (e as Error).message); }
    }

    if (!summary) return null;
    const mine = items.find(r => r.author_user_id === me.id);

    return (
        <section style={{ padding: 14, border: '1px solid #cfc9b7', margin: '18px 0' }}>
            <h2 style={{ margin: 0, fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2.5, borderBottom: '1px solid var(--ink)', paddingBottom: 4 }}>
                Reviews
            </h2>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', margin: '10px 0 6px' }}>
                <span style={{ fontSize: 28, fontFamily: 'Newsreader,serif', fontWeight: 700 }}>
                    {summary.count === 0 ? '—' : summary.average.toFixed(1)}
                </span>
                <span style={{ fontSize: 22, letterSpacing: 2 }}>{renderStars(summary.average)}</span>
                <span style={{ fontSize: 12, color: '#666' }}>
                    {summary.count} {summary.count === 1 ? 'review' : 'reviews'}
                </span>
            </div>

            {isOwner && (
                <div style={{ padding: 10, background: '#faf8f2', border: '1px dashed #999', margin: '10px 0', fontFamily: "'Newsreader',serif", fontStyle: 'italic', color: '#555' }}>
                    You own this listing — you can't review your own place. Share the page
                    URL with your customers to invite reviews. (When we ship visit-tracking
                    +1 notifications later, this will happen automatically.)
                </div>
            )}
            {!isOwner && (
                <div style={{ padding: 10, background: '#faf8f2', border: '1px dashed #999', margin: '10px 0' }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                        {mine ? 'Update your review:' : 'Your review:'}
                    </div>
                    <StarInput value={draftR} onChange={setDraftR} />
                    <textarea rows={3}
                              placeholder="How was it? (optional)"
                              value={draftB} onChange={e => setDraftB(e.target.value)}
                              style={{ width: '100%', padding: 8, marginTop: 6, fontFamily: 'inherit', border: '1px solid #999' }} />
                    <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
                        <button type="button" onClick={submit}
                                style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 0, cursor: 'pointer', fontFamily: '-apple-system,sans-serif', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                            {mine ? 'Update review' : 'Post review'}
                        </button>
                        {mine && (
                            <button type="button" onClick={remove}
                                    style={{ padding: '6px 12px', background: 'none', color: '#8b1919', border: '1px solid #8b1919', cursor: 'pointer', fontSize: 11 }}>
                                Delete mine
                            </button>
                        )}
                        {status && <span style={{ fontSize: 12, color: status.startsWith('Failed') ? '#8b1919' : '#2b5e3f' }}>{status}</span>}
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <p style={{ color: '#999', fontStyle: 'italic', fontFamily: "'Newsreader',serif", marginTop: 12 }}>
                    No reviews yet.
                </p>
            ) : (
                <div style={{ marginTop: 12 }}>
                    {items.map(r => (
                        <div key={r.id} style={{ padding: '8px 0', borderBottom: '1px dotted #cfc9b7' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                <span style={{ fontSize: 14, letterSpacing: 1 }}>{renderStars(r.rating)}</span>
                                <Link to={'/user/' + r.author_user_id} style={{ fontWeight: 600, fontSize: 13 }}>
                                    {r.author_name}
                                </Link>
                                <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>
                                    {timeAgo(r.created_at)}
                                </span>
                            </div>
                            {r.body && (
                                <div style={{ fontFamily: "'Newsreader',serif", fontSize: 14, color: '#333', marginTop: 4 }}>
                                    {r.body}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function renderStars(v: number): string {
    // Half-star approximation: round to nearest 0.5, render solid/half/empty.
    const rounded = Math.round(v * 2) / 2;
    let out = '';
    for (let i = 1; i <= 5; i++) {
        if      (rounded >= i)         out += '★';
        else if (rounded + 0.5 === i)  out += '⯨';   // half-star fallback
        else                           out += '☆';
    }
    return out;
}

function StarInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
    return (
        <div style={{ fontSize: 26, letterSpacing: 4, cursor: 'pointer', userSelect: 'none', color: '#e0aa00' }}>
            {[1,2,3,4,5].map(i => (
                <span key={i} onClick={() => onChange(i)} title={i + ' star' + (i > 1 ? 's' : '')}>
                    {value >= i ? '★' : '☆'}
                </span>
            ))}
        </div>
    );
}

function StatCard({ label, total, delta }: { label: string; total: number; delta: number }) {
    return (
        <div className="stat-card">
            <div className="stat-total">{total}</div>
            <div className="stat-label">{label}</div>
            <div className="stat-delta">+{delta} in last 30d</div>
        </div>
    );
}

function BarChart({ data, accent }: { data: Array<{ week_start: string; count: number }>; accent?: boolean }) {
    const max = Math.max(1, ...data.map(d => d.count));
    return (
        <div className="bar-chart">
            {data.map(d => {
                const pct = (d.count / max) * 100;
                const date = new Date(d.week_start);
                return (
                    <div className="bar-col" key={d.week_start}>
                        <div className="bar-fill-wrap">
                            <div
                                className={'bar-fill' + (accent ? ' accent' : '')}
                                style={{ height: pct + '%' }}
                                title={`${d.count} in week of ${date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}`}
                            />
                        </div>
                        <div className="bar-count">{d.count}</div>
                        <div className="bar-label">{date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</div>
                    </div>
                );
            })}
        </div>
    );
}

// BusinessInfoEditor — owner-only form for the fields that show up on
// /groups/:slug. Category, address, phone, email, website, hours (one
// string per day-of-week). Description + name reuse the existing
// updateRequest but aren't exposed here yet — those need their own UX
// pass since name changes cascade through URLs.
//
// Save writes back via PATCH /api/groups/:slug and reloads the parent
// so the profile page across a tab-switch reflects the new state.
const HOURS_DAYS = ['mon','tue','wed','thu','fri','sat','sun'] as const;
const HOURS_LABEL: Record<typeof HOURS_DAYS[number], string> = {
    mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
    fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

function BusinessInfoEditor({ group, onSaved }: { group: GroupInfo; onSaved: () => Promise<void> }) {
    const api = useApi();
    const [category, setCategory] = useState(group.category ?? '');
    const [address,  setAddress]  = useState(group.address  ?? '');
    const [phone,    setPhone]    = useState(group.phone    ?? '');
    const [email,    setEmail]    = useState(group.email    ?? '');
    const [website,  setWebsite]  = useState(group.website  ?? '');
    const [hours,    setHours]    = useState<Record<string, string>>({ ...(group.hours ?? {}) });
    const [saving,   setSaving]   = useState(false);
    const [err,      setErr]      = useState('');
    const [saved,    setSaved]    = useState(false);

    async function save() {
        setSaving(true); setErr(''); setSaved(false);
        try {
            // Only send fields the user actually touched — send the ones
            // shown in the form regardless, backend treats "" as clear.
            // Hours: collapse empty strings out so the JSONB doesn't fill
            // with blank keys.
            const trimmedHours: Record<string, string> = {};
            for (const d of HOURS_DAYS) {
                const v = (hours[d] ?? '').trim();
                if (v) trimmedHours[d] = v;
            }
            await api.patch(`/api/groups/${encodeURIComponent(group.slug)}`, {
                category, address, phone, email, website,
                hours: trimmedHours,
            });
            setSaved(true);
            await onSaved();
        } catch (e) {
            setErr((e as Error).message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <section className="dashboard-block">
            <h2>Business info</h2>
            <p style={{ margin: '0 0 12px', color: '#666', fontSize: 12 }}>
                What visitors see on <code>/groups/{group.slug}</code>. Only you can edit this.
            </p>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', maxWidth: 640 }}>
                <Field label="Category">
                    <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
                        <option value="">— pick one —</option>
                        {['cafe','restaurant','retail','salon','service','grocery','community','other'].map(c =>
                            <option key={c} value={c}>{c}</option>
                        )}
                    </select>
                </Field>
                <Field label="Address">
                    <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Street + suburb" style={inputStyle} />
                </Field>
                <Field label="Phone">
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+27…" style={inputStyle} />
                </Field>
                <Field label="Email">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="hello@…" style={inputStyle} />
                </Field>
                <Field label="Website">
                    <input type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://…" style={inputStyle} />
                </Field>
            </div>

            <h3 style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#666', marginTop: 20, marginBottom: 8 }}>
                Opening hours
            </h3>
            <div style={{ display: 'grid', gap: 6, maxWidth: 480 }}>
                {HOURS_DAYS.map(d => (
                    <div key={d} style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 12, alignItems: 'center' }}>
                        <label style={{ fontSize: 13 }}>{HOURS_LABEL[d]}</label>
                        <input type="text" value={hours[d] ?? ''} onChange={e => setHours(h => ({ ...h, [d]: e.target.value }))}
                               placeholder="e.g. 8am–4pm  or  Closed"
                               style={inputStyle} />
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" className="plain" onClick={save} disabled={saving}
                        style={{ padding: '8px 20px', background: 'var(--ink)', color: '#fff', border: 0, cursor: saving ? 'default' : 'pointer', textTransform: 'uppercase', letterSpacing: 1, fontSize: 12 }}>
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
                {saved && <span style={{ color: '#2b5e3f', fontSize: 12 }}>✓ Saved</span>}
                {err   && <span style={{ color: '#8b1919', fontSize: 12 }}>{err}</span>}
            </div>
        </section>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: '#666' }}>{label}</span>
            {children}
        </label>
    );
}

const inputStyle: React.CSSProperties = {
    padding: '6px 10px', border: '1px solid #cfc9b7', background: '#fff',
    fontSize: 14, fontFamily: 'inherit',
};
